import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { PlansOverviewResponse, PlansOverviewRow } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Loading, PageTitle, Tabs } from "../components/ui";
import { api, errorText } from "../lib/api";
import { useMe } from "../lib/session";

type View = "draft" | "overdue" | "paused" | "none" | "all";

const inView: Record<View, (r: PlansOverviewRow) => boolean> = {
  draft: (r) => !!r.draftId,
  overdue: (r) => r.overdue > 0,
  paused: (r) => !!r.approved?.pausedSince,
  none: (r) => !r.approved && !r.draftId,
  all: () => true,
};

/**
 * Plans (docs/09 §2): every active student in scope with their plan state, so drafts waiting for approval,
 * students behind their plan, paused plans and students without a plan are found in one place.
 */
export function PlansPage() {
  const { data: me } = useMe();
  const [view, setView] = useState<View>("draft");
  const q = useQuery({
    queryKey: ["plans-overview", me?.activeRole],
    queryFn: () => api<PlansOverviewResponse>("/v1/plans/overview"),
  });
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const count = (v: View) => rows.filter(inView[v]).length;
  const shown = rows.filter(inView[view]);

  return (
    <>
      <PageTitle
        title="الخطط"
        sub="خطط الطلاب النشطين ضمن نطاقك: ما ينتظر الاعتماد، ومن تأخر عن خطته، والخطط المتوقفة، ومن لا خطة له."
      />
      <Tabs<View>
        label="حالة الخطة"
        value={view}
        onChange={setView}
        items={[
          { value: "draft", label: "بانتظار الاعتماد", count: count("draft") },
          { value: "overdue", label: "متأخرون", count: count("overdue") },
          { value: "paused", label: "متوقفة", count: count("paused") },
          { value: "none", label: "بلا خطة", count: count("none") },
          { value: "all", label: "الكل", count: rows.length },
        ]}
      />
      <section className="card">
        <ErrorNote error={q.error ? errorText(q.error) : null} />
        {q.isPending ? (
          <Loading rows={4} />
        ) : !shown.length ? (
          <Empty title="لا طلاب هنا" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الطالب</th>
                  <th>الحلقة</th>
                  <th>الخطة</th>
                  <th>المتأخر</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.enrollmentId}>
                    <td>
                      <Link to="/users/$userId" params={{ userId: r.student.id }}>
                        <b>{r.student.name}</b>
                      </Link>
                    </td>
                    <td>{r.circle.name}</td>
                    <td>
                      {r.approved ? (
                        r.approved.pausedSince ? (
                          <Badge tone="warning">
                            متوقفة منذ <span className="num">{r.approved.pausedSince}</span>
                          </Badge>
                        ) : (
                          <Badge tone="success">معتمدة</Badge>
                        )
                      ) : (
                        <Badge tone="neutral">لا خطة معتمدة</Badge>
                      )}{" "}
                      {r.draftId && <Badge tone="info">مسودة بانتظار الاعتماد</Badge>}
                      {r.draftId && r.draftProposedBy && (
                        <div className="muted small">مقترحة من المعلم: {r.draftProposedBy}</div>
                      )}
                    </td>
                    <td className="num">{r.overdue ? `${r.overdue} بند` : "—"}</td>
                    <td className="actions">
                      <Link
                        className="btn btn-ghost btn-sm"
                        to="/plan-preview"
                        search={{ enrollmentId: r.enrollmentId }}
                      >
                        {r.draftId ? "مراجعة واعتماد" : r.approved ? "الخطة" : "إعداد خطة"}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
